import { useMemo, useState } from "react";
import { Download, FilePlus2, Loader2, Paperclip, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";
import { attachmentDownloadUrl } from "@/api/contracts";
import { downloadAttachment } from "@/api/taskAttachments";
import { uploadAttachment } from "@/api/attachments";
import { useUploadContractAttachment } from "@/hooks/useContracts";
import { AnnexWizardModal } from "./annex/AnnexWizardModal";
import { DOCUMENT_TYPE_LABEL } from "./constants";
import type {
  ContractAttachmentDef,
  ContractDocumentType,
} from "@/api/types";

interface Props {
  contractId: string;
  attachments: ContractAttachmentDef[];
  canUpload: boolean;
  /** D-143: route-контекст для guard-діалогу «Відкрити картку клієнта». */
  holdingId?: string | null;
}

const UPLOADABLE_TYPES: ContractDocumentType[] = [
  "signed_scan",
  "appendix",
  "additional_agreement",
  "specification",
  "protocol_of_disagreements",
  "termination_notice",
  "amendment",
  "other",
];

export function AttachmentsList({ contractId, attachments, canUpload, holdingId }: Props) {
  const grouped = useMemo(() => {
    const map = new Map<string, ContractAttachmentDef[]>();
    for (const a of attachments) {
      const key = a.documentType;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(a);
    }
    return map;
  }, [attachments]);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [scanUploadOpen, setScanUploadOpen] = useState(false); // смоук-фікс Igor
  const [annexOpen, setAnnexOpen] = useState(false); // D-143
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Token-header auth only (no session cookie) — a bare <a href> 401s and stock
  // Espo returns WWW-Authenticate: Basic on a non-XHR GET (native login popup).
  // Fetch authed bytes via header-auth → object URL download.
  async function onDownload(attachmentId: string, fileName: string) {
    setDownloadingId(attachmentId);
    setDownloadError(null);
    try {
      await downloadAttachment(attachmentDownloadUrl(contractId, attachmentId), fileName);
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : "Не вдалося завантажити");
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Документи
        </h3>
        {canUpload && (
          <div className="flex items-center gap-1.5">
            {/* D-143 §0 — CTA майстра додаткової угоди */}
            <Button
              size="sm"
              variant="outline"
              className="h-9 gap-1.5 border-primary/40 text-primary hover:bg-primary/10"
              onClick={() => setAnnexOpen(true)}
            >
              <FilePlus2 className="w-3.5 h-3.5" /> Створити додаткову угоду
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-9 gap-1.5"
              onClick={() => setUploadOpen(true)}
            >
              <Upload className="w-3.5 h-3.5" /> Завантажити
            </Button>
          </div>
        )}
      </div>

      {attachments.length === 0 ? (
        <div className="rounded-lg border bg-muted/30 p-4 text-center">
          <Paperclip className="mx-auto w-4 h-4 text-muted-foreground mb-2" aria-hidden />
          <p className="text-xs text-muted-foreground">Додаткових документів ще немає</p>
          {canUpload && (
            <Button
              size="sm"
              variant="outline"
              className="mt-2 h-8 gap-1.5 border-primary/40 text-primary hover:bg-primary/10"
              onClick={() => setAnnexOpen(true)}
            >
              <FilePlus2 className="w-3.5 h-3.5" /> Створити додаткову угоду
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {Array.from(grouped.entries()).map(([type, items]) => (
            <section key={type}>
              <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {DOCUMENT_TYPE_LABEL[type as ContractDocumentType] ?? type}
              </p>
              <ul className="space-y-1">
                {items.map((a) => (
                  <li
                    key={a.attachmentId}
                    className="flex items-center gap-2 px-3 py-2 rounded-md border bg-card"
                  >
                    <Paperclip className="w-3.5 h-3.5 text-muted-foreground" aria-hidden />
                    {/* смоук-фікс Igor: імʼя файла клікабельне → той самий download (AC-14, §0) */}
                    <button
                      type="button"
                      disabled={downloadingId === a.attachmentId}
                      onClick={() => void onDownload(a.attachmentId, a.fileName)}
                      className="flex-1 truncate text-left text-sm hover:text-primary hover:underline disabled:opacity-50"
                      title={`Скачати ${a.fileName}`}
                    >
                      {a.fileName}
                      {/* D-143 AC-16d: номер і дата — з metadata */}
                      {a.annexNumber && (
                        <span className="ml-1.5 text-xs text-muted-foreground">
                          №{a.annexNumber}{a.documentDate ? ` від ${a.documentDate.split("-").reverse().join(".")}` : ""}
                        </span>
                      )}
                    </button>
                    {a.uploadedBy && (
                      <span className="text-xs text-muted-foreground truncate max-w-[120px]">
                        {a.uploadedBy.name}
                      </span>
                    )}
                    <button
                      type="button"
                      disabled={downloadingId === a.attachmentId}
                      onClick={() => void onDownload(a.attachmentId, a.fileName)}
                      className="inline-flex items-center justify-center rounded-md p-1.5 text-primary hover:bg-primary/10 disabled:opacity-50"
                      aria-label={`Завантажити ${a.fileName}`}
                    >
                      {downloadingId === a.attachmentId ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
                      ) : (
                        <Download className="w-3.5 h-3.5" aria-hidden />
                      )}
                    </button>
                    {/* смоук-фікс Igor: видима наступна дія петлі (§20.6) */}
                    {canUpload && (
                      <button
                        type="button"
                        onClick={() => setScanUploadOpen(true)}
                        className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-2 py-1 text-xs text-muted-foreground hover:border-primary/40 hover:text-primary"
                        title="Завантажити підписаний скан"
                      >
                        <Upload className="w-3 h-3" aria-hidden />Підписаний скан
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      {downloadError && (
        <p role="alert" className="text-xs text-destructive">
          {downloadError}
        </p>
      )}

      {uploadOpen && (
        <UploadDialog
          contractId={contractId}
          onClose={() => setUploadOpen(false)}
        />
      )}

      {scanUploadOpen && (
        <UploadDialog
          contractId={contractId}
          initialType="signed_scan"
          onClose={() => setScanUploadOpen(false)}
        />
      )}

      {annexOpen && (
        <AnnexWizardModal
          contractId={contractId}
          holdingId={holdingId}
          onClose={() => setAnnexOpen(false)}
        />
      )}
    </div>
  );
}

export function UploadDialog({
  contractId,
  onClose,
  initialType = "appendix",
}: {
  contractId: string;
  onClose: () => void;
  /** смоук-фікс Igor: пресет типу для «Завантажити підписаний скан». */
  initialType?: ContractDocumentType;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState<ContractDocumentType>(initialType);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const uploadMut = useUploadContractAttachment(contractId);

  const submit = async () => {
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const att = await uploadAttachment(file, "CContractAttachment", "file");
      await uploadMut.mutateAsync({ fileId: att.id, documentType });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не вдалося завантажити");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      aria-hidden
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg border bg-background shadow-2xl"
      >
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h4 className="text-sm font-semibold">Завантажити документ</h4>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрити"
            className="rounded-md p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-4 py-4 space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium">Тип документа</label>
            <select
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value as ContractDocumentType)}
              className="flex h-9 w-full rounded-md border bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {UPLOADABLE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {DOCUMENT_TYPE_LABEL[t]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium">
              Файл <span className="text-destructive">*</span>
            </label>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.tif,.tiff"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className={cn(
                "block w-full text-sm",
                "file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-primary-foreground hover:file:bg-primary/90",
              )}
            />
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
        <div className="border-t px-4 py-3 flex items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={uploading}>
            Скасувати
          </Button>
          <Button type="button" onClick={submit} disabled={!file || uploading}>
            {uploading ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Завантаження…
              </>
            ) : (
              "Завантажити"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
